
/**
 * The control-size scale: one set of heights every interactive control is
 * sized from.
 *
 * ## Why this exists
 *
 * Before this module each control override typed its own height. Buttons were
 * 32px, fields were 32px, a small IconButton came out at ~22px and a medium
 * one at 40px, and at phone widths buttons grew to 40-46px while fields stayed
 * at 32. So any row mixing them was misaligned, and the only fix available
 * at a call site was a one-off `sx` nudge that the next row didn't get.
 *
 * Now a control asks for a SIZE STEP, never a pixel value, and every control
 * of the same step is the same height:
 *
 * | Step | Used by                                                    |
 * |------|------------------------------------------------------------|
 * | `sm` | small Button and IconButton (dense rows, adornments)       |
 * | `md` | Button (the default), TextField, Select, Autocomplete,     |
 * |      | date/time pickers, ToggleButton, medium IconButton         |
 * | `lg` | large Button, large IconButton                             |
 *
 * The `size` prop maps straight onto the step: small = `sm`, medium = `md`,
 * large = `lg`. (Until this was fixed, admin defaulted Buttons to `small` and
 * made small and medium the same height, so `small` meant "normal" and
 * `medium` was a near-duplicate nobody used.)
 *
 * `md` is the one that matters: a field, a button and an icon button placed in
 * one row line up with no help.
 *
 * ## Touch
 *
 * Below the `sm` breakpoint every step grows to its `touch` value, and so
 * does what is inside it: label, glyph and chip sizes all have touch values.
 * No step is under 44px there, the iOS minimum tap target, and `md` is 44px
 * for buttons AND fields together, which is what keeps them aligned on a
 * phone. A control's box alone growing was not enough: a 44px button with a
 * 14px label and a 20px glyph still read as a desktop control on a phone.
 *
 * ## What this does NOT fix
 *
 * A TextField renders its label ABOVE the field (see inputComponents.ts), so
 * the FormControl is taller than the field itself. In a row of labelled fields
 * and buttons, `alignItems: 'center'` centres the button against label+field
 * and lifts it ~10px. Align such rows to `flex-end`, or use `hiddenLabel` for
 * inline toolbar fields. Heights can't solve that; layout has to.
 */

export type ControlStep = 'sm' | 'md' | 'lg';
export type ControlScale = Record<ControlStep, number>;

/**
 * The trailing icon inside a field: a Select's chevron, Autocomplete's
 * toggle and clear buttons, a picker's calendar/clock button, a password
 * field's eye. One glyph size and one distance from the field's right edge
 * for all of them. A clickable one keeps a hit area (`box`) around the glyph,
 * and the field's right padding is derived so the glyph still lands on `inset`.
 */
export interface FieldIconGeometry {
  /** Glyph size (px). */
  glyph: number;
  /** Glyph box's distance from the field's right edge (px). */
  inset: number;
  /** Hit area of a clickable trailing icon, desktop / touch (px). */
  box: number;
  touchBox: number;
}

/**
 * Table geometry. Rows are sized by HEIGHT, never by padding round whatever
 * the cells happen to hold: with padding alone a text row was 32px and a row
 * carrying a 32px IconButton was 44px, so no two tables (or two rows of one)
 * agreed. A table cell's `height` behaves as a minimum, so taller content
 * (a two-line row with an avatar) still grows the row; it just can't make an
 * ordinary row a different height from its neighbours.
 */
export interface TableGeometry {
  /** Header row height (px). Deliberately shorter than a body row. */
  head: number;
  /** Body row height, desktop / touch (px). Each fits one `md` control; the cell padding is what is left over. */
  body: number;
  touchBody: number;
  /** Line height of header AND body text (px), so the two can't size differently. */
  lineHeight: number;
  /** Cell side padding (px). */
  paddingX: number;
}

/**
 * A Switch: track size and a round thumb, inset 2px. Width follows from the
 * thumb's travel, so a shorter switch is a narrower one too.
 */
export interface SwitchGeometry {
  width: number;
  height: number;
  thumb: number;
}

/**
 * A dropdown: Menu, the Select's list and the Autocomplete list alike. Rows are
 * the `md` control height (touch: `touchControl.md`), set in an inset so the
 * hover reads as a rounded chip rather than a full-width stripe (the operator
 * console's Linear/Raycast idiom, now shared).
 */
export interface MenuGeometry {
  /** Corner radius of the floating surface (px). An item is `radius - inset`, so the curves are concentric. */
  radius: number;
  /** Gap between the surface edge and an item's hover shape, on every side (px). */
  inset: number;
  /**
   * Where item text starts, from the surface's OUTER edge (px). Equal to a
   * field's text inset (1px border + 12px padding), so a Select's options
   * line up with the value shown in the field above them.
   */
  textInset: number;
  /** Section header row height (px). */
  subheaderHeight: number;
  /** Leading icon glyph, and the gap from it to the label (px). */
  icon: number;
  iconGap: number;
  /** Row text size and line height, desktop and touch (px). Touch matches the live app's 15px rows. */
  fontSize: number;
  lineHeight: number;
  touchFontSize: number;
  touchLineHeight: number;
  /** Leading icon glyph at touch width (px), grown with the text. */
  touchIcon: number;
  /** Gap between the button or field and the surface it opens (px). */
  offset: number;
}

/**
 * A page's header (AdminHeader): the space above the title (smaller at phone
 * width), the space from the header to the page's content, and the gap
 * between the breadcrumb row and the title. The header owns that space, so a
 * page's content starts flush rather than adding its own (it had added 8 to
 * 28px more, so content began 32 to 60px below the title).
 */
export interface PageHeaderGeometry {
  padding: number;
  touchPadding: number;
  crumbGap: number;
  /** Between the title (or its description) and tabs that belong to the header. */
  tabsGap: number;
}

/**
 * An empty state (EmptyState): the icon tile, the gaps between tile, title,
 * description and action, and the padding round it. `page` is a list page
 * with nothing on it yet; `compact` is a section or card with nothing in it.
 * They had been 16 to 40px of padding with 14 to 64px icons, by call site.
 */
export interface EmptyStateSize {
  padding: number;
  tile: number;
  icon: number;
  /** Tile to title, and description to action (px). */
  gap: number;
  /** Title to description (px). */
  titleGap: number;
  titleSize: number;
}
export interface EmptyStateGeometry {
  page: EmptyStateSize;
  compact: EmptyStateSize;
  /** Widest the description runs, so it reads as one or two lines (px). */
  descriptionMaxWidth: number;
  tileRadius: number;
}

/**
 * A card: a bordered surface on the page. One inset round its content (the
 * dialog's, so a card and a dialog read as the same kind of surface) and one
 * gap between stacked cards. Corners are the floating surfaces' 8px, so they
 * stay concentric round the 6px controls inside; `theme.shape` (4px) is still
 * every other surface's.
 */
export interface CardGeometry {
  radius: number;
  /** Inset round the content and actions, desktop and touch (px). */
  padding: number;
  touchPadding: number;
  /** Space between stacked or side-by-side cards (px). */
  gap: number;
}

/**
 * A dialog: one inset round its content and one gap between title, content
 * and actions, so the space under a title and above the buttons is the theme's
 * rather than each dialog's `pb` / `pt` nudge.
 */
export interface DialogGeometry {
  /** Corner radius (px); the same as a menu, the other floating surface. */
  radius: number;
  /** Inset round the title, content and actions, desktop and touch (px). */
  padding: number;
  touchPadding: number;
  /** Gap between title, content and actions (px). */
  gap: number;
  /** Title line height (px); the close button centres on it. */
  titleLine: number;
  /** Space between the dialog and the viewport edge at touch width (px). Desktop keeps MUI's 32. */
  touchMargin: number;
}

/**
 * The Operator console's Pane: a dockable zone with a fixed-height header and
 * one inset round its content. Deliberately tighter than a card (12px against
 * 20px), because the console is a booth instrument that has to fit six Panes
 * on a laptop, but on the same grid. Its corners are a card's, so a Pane and a
 * card read as one kind of surface.
 */
export interface PaneGeometry {
  /** Header height (px), whatever the header holds: its actions are `sm` controls. */
  header: number;
  /** Header height at touch width (px): an `sm` control is 44px there, plus room round it. */
  touchHeader: number;
  /** Header side padding and the inset round the content (px). */
  padding: number;
  /** Space between Panes and round the docked Layout (px). Also the resize seam's width. */
  gap: number;
  /** A list row inside a Pane (audience, activity): the `md` control step (px). */
  row: number;
  /** A Running order row: a 36px avatar with 4px above and below (px). */
  actRow: number;
  /** Corner radius of a row's hover and selection fill (px), the menu row's. */
  rowRadius: number;
}

export interface ControlDensity {
  /** Control height per step at desktop widths (px). */
  control: ControlScale;
  /** Control height per step below the touch breakpoint (px). */
  touchControl: ControlScale;
  /** Glyph size inside an IconButton, per step (px). Desktop. */
  icon: ControlScale;
  /**
   * Glyph size inside an IconButton, per step (px). Touch. A 20px glyph in a
   * 44px box reads as a desktop icon on a stretched hit area; iOS draws its
   * toolbar glyphs at about 22-24pt.
   */
  touchIcon: ControlScale;
  /** Horizontal padding inside a Button, per step (px). Desktop. */
  buttonPaddingX: ControlScale;
  /** Horizontal padding inside a Button, per step (px). Touch. */
  touchButtonPaddingX: ControlScale;
  /** Button label size per step. Desktop. */
  buttonFontSize: ControlScale;
  /** Button label size per step. Touch. */
  touchButtonFontSize: ControlScale;
  /** A Button's start/end icon glyph per step (px). Desktop. */
  buttonIcon: ControlScale;
  /** A Button's start/end icon glyph per step (px). Touch: it grows with the label. */
  touchButtonIcon: ControlScale;
  /**
   * A Button's minimum width per step, desktop and touch (px). MUI's 64px
   * suits an ordinary button (a bare "OK" shouldn't look stubby) and swamps a
   * compact one: a small "Edit" needs ~43px and was stretched to 64. So `sm`'s
   * minimum is its own height, never narrower than it is tall.
   */
  buttonMinWidth: ControlScale;
  touchButtonMinWidth: ControlScale;
  /** Chip heights. Chips are labels, not controls, so they sit below `sm`. */
  chip: { small: number; medium: number };
  /** Chip heights at touch width: still below a control, but a label you can read on a phone. */
  touchChip: { small: number; medium: number };
  /** Chip label size, desktop and touch (px). */
  chipFontSize: number;
  touchChipFontSize: number;
  /**
   * Gap between controls in one row (FormRow), desktop and touch (px). It
   * grows with the controls: 8px beside a 32px control reads as a normal
   * toolbar, the same 8px beside a 44px one reads cramped.
   */
  rowGap: number;
  touchRowGap: number;
  /**
   * Vertical rhythm of a form (FormStack), the same at every width (px).
   * `fieldGap` is between stacked fields, measured from the bottom of one
   * field (its helper text included) to the next field's label; `groupGap`
   * is between groups of fields. Fields were 4 to 16px apart by call site,
   * and 8px left a helper text touching the next label.
   */
  fieldGap: number;
  groupGap: number;
  /**
   * Widest a page's form column runs (px). Fields fill the column, not the
   * window: a name field 1200px wide is hard to scan and tells the user to
   * write a paragraph. Dialogs don't need it (the dialog is the column).
   */
  formMaxWidth: number;
  /**
   * Corner radius of every interactive control: Buttons, IconButton hover
   * shapes, fields (search fields included), pickers, ToggleButtons (px).
   * One value, so a button and the field beside it can't round differently.
   * Chips and badges are NOT controls and stay pills; that difference is how
   * a status label reads as something other than a button.
   */
  controlRadius: number;
  /** Trailing icon inside a field; see FieldIconGeometry. */
  fieldIcon: FieldIconGeometry;
  /** Table header and row heights; see TableGeometry. */
  table: TableGeometry;
  /**
   * The Switch at desktop and touch widths. Desktop is the 20px row a 13px
   * label sits in (shadcn's and Radix's size); touch keeps the 42x24 iOS
   * proportions, where the target matters more than the look.
   */
  switch: SwitchGeometry;
  touchSwitch: SwitchGeometry;
  /** Menus, Select lists and Autocomplete lists; see MenuGeometry. */
  menu: MenuGeometry;
  /** Dialogs; see DialogGeometry. */
  dialog: DialogGeometry;
  /** Cards and outlined Papers; see CardGeometry. */
  card: CardGeometry;
  /** Page headers; see PageHeaderGeometry. */
  pageHeader: PageHeaderGeometry;
  /** Empty states; see EmptyStateGeometry. */
  emptyState: EmptyStateGeometry;
  /** Operator console Panes; see PaneGeometry. */
  pane: PaneGeometry;
}

/**
 * For a clickable trailing icon: the padding inside its hit area, and the
 * field's right padding that puts the glyph on `inset`. Returned for both
 * desktop and touch because the hit area grows on touch and the field's
 * padding has to shrink by the same amount.
 */
export const fieldIconLayout = (g: FieldIconGeometry, fieldBorder: number) => {
  const pad = (g.box - g.glyph) / 2;
  const touchPad = (g.touchBox - g.glyph) / 2;
  // `inset` is measured from the field's OUTER edge, which is where the eye
  // reads it. CSS `right` and padding both start inside the field's border.
  const inside = g.inset - fieldBorder;
  return {
    pad,
    touchPad,
    /** `right` for a non-clickable glyph (the Select chevron). */
    glyphRight: inside,
    fieldPaddingRight: Math.max(0, inside - pad),
    touchFieldPaddingRight: Math.max(0, inside - touchPad),
  };
};

/**
 * Dense desktop console density: admin + the Electron operator console.
 * Font sizes are px here (13 / 14 / 15) and emitted as rem by the theme.
 */
export const CONSOLE_DENSITY: ControlDensity = {
  control: { sm: 24, md: 32, lg: 40 },
  // On a phone nothing is under 44px, Apple's minimum tap target: `sm` and
  // `md` share a height there and differ only in their label and padding.
  // `sm` was 32px, which put every row action and small icon button under
  // the minimum, and read as a desktop control on a phone.
  touchControl: { sm: 44, md: 44, lg: 48 },
  icon: { sm: 18, md: 20, lg: 24 },
  touchIcon: { sm: 22, md: 24, lg: 24 },
  // `md` is the ordinary button (the admin default); `sm` is a genuinely
  // compact one for dense rows. `md` keeps exactly the look `size="small"`
  // used to have, when small was the default and medium was an unused alias.
  // Desktop side padding was 16 / 22 when buttons were pills, whose rounded
  // ends eat horizontal room; on a 6px rectangle that read roomy. 12 / 16 is
  // the usual pairing for 32 / 40px buttons. Touch keeps its wider values.
  buttonPaddingX: { sm: 10, md: 12, lg: 16 },
  touchButtonPaddingX: { sm: 14, md: 16, lg: 24 },
  buttonFontSize: { sm: 12, md: 13, lg: 15 },
  // A 44px button with a 14px label is a phone-sized box with desktop-sized
  // text, which is what made phone controls read as small. 16px is the input
  // text size (iOS zooms below it), so a button and the field beside it now
  // carry text of one size.
  touchButtonFontSize: { sm: 15, md: 16, lg: 17 },
  // 16px matches the marketing site (shadcn Button: `[&_svg]:size-4`). It was
  // 18 beside a 13px label, which read heavier than the text it annotates.
  buttonIcon: { sm: 14, md: 16, lg: 16 },
  touchButtonIcon: { sm: 16, md: 18, lg: 20 },
  buttonMinWidth: { sm: 24, md: 64, lg: 64 },
  touchButtonMinWidth: { sm: 44, md: 64, lg: 64 },
  chip: { small: 20, medium: 24 },
  touchChip: { small: 24, medium: 28 },
  chipFontSize: 12,
  touchChipFontSize: 13,
  rowGap: 8,
  touchRowGap: 12,
  // The dialog's and the card's gap, so a dialog's fields keep its rhythm.
  fieldGap: 16,
  groupGap: 32,
  formMaxWidth: 640,
  // Matches the marketing site (apps/public), whose Button and Input are both
  // shadcn's `rounded-md`: calc(var(--radius) - 2px) with --radius 0.5rem.
  // If that token moves, move this with it.
  controlRadius: 6,
  // Before this, trailing icons sat anywhere from 8 to 20px off the edge at
  // four glyph sizes, because MUI's Select, Autocomplete, the pickers and
  // InputAdornment each position their own. 16px matches button icons.
  fieldIcon: { glyph: 16, inset: 10, box: 24, touchBox: 32 },
  // The header was 34.8px and rows 31.6px: MUI gives header cells their own
  // 24px line height, so equal padding still produced unequal boxes.
  table: { head: 32, body: 40, touchBody: 48, lineHeight: 20, paddingX: 12 },
  switch: { width: 36, height: 20, thumb: 16 },
  touchSwitch: { width: 42, height: 24, thumb: 20 },
  // Rows were 41px with 20px padding and 14px text (33px / 16px in an
  // Autocomplete), and option text sat 8px right of the field's own text.
  menu: {
    radius: 8, inset: 4, textInset: 13, subheaderHeight: 28, icon: 16, iconGap: 8, offset: 4,
    fontSize: 13, lineHeight: 20, touchFontSize: 15, touchLineHeight: 22, touchIcon: 20,
  },
  // Was 16px all round and 4px corners, with 12px under the title, 24px above
  // the buttons, and a dozen dialogs nudging the difference by hand.
  dialog: { radius: 8, padding: 20, touchPadding: 16, gap: 16, titleLine: 24, touchMargin: 16 },
  // Card padding ran from 6px to 20px, mostly because call sites wrote values
  // for MUI's 8px spacing unit in a 4px theme (`p: 3` meant 24, rendered 12),
  // and corners were 4px round 6px controls.
  card: { radius: 8, padding: 20, touchPadding: 16, gap: 16 },
  pageHeader: { padding: 32, touchPadding: 16, crumbGap: 4, tabsGap: 16 },
  emptyState: {
    page: { padding: 48, tile: 48, icon: 24, gap: 16, titleGap: 4, titleSize: 16 },
    compact: { padding: 24, tile: 36, icon: 18, gap: 12, titleGap: 4, titleSize: 13 },
    descriptionMaxWidth: 400,
    tileRadius: 8,
  },
  // Pane spacing had been written for an 8px unit in a 4px theme, so headers
  // sat 6px from the edge and ran 33 to 41px tall depending on their actions,
  // content was inset 6px and Panes were 4px apart.
  pane: { header: 36, touchHeader: 52, padding: 12, gap: 8, row: 32, actRow: 44, rowRadius: 4 },
};

/**
 * The breakpoint below which controls switch to their touch heights: exactly
 * what MUI's `theme.breakpoints.down('sm')` produces (sm is 600px, and MUI
 * subtracts 0.05). Written out rather than derived, so this file imports
 * nothing and the public site's Tailwind config can read it (tailwindPreset.ts).
 * `tests/unit/density.test.ts` asserts it still equals MUI's, so it can't drift.
 * The Showliner themes don't customise breakpoints; if one ever does, this
 * must follow that theme.
 */
export const TOUCH_MEDIA = '@media (max-width:599.95px)';

/** `16px` → `1rem`, against the browser's 16px root (the themes don't change it). */
export const pxToRem = (px: number): string => `${px / 16}rem`;

/**
 * Rubik's vertical metrics in font units, read from the font file's `hhea` and
 * `OS/2` tables (the Adobe Fonts build the apps load; USE_TYPO_METRICS is off,
 * so browsers lay lines out with the hhea ascent and descent).
 */
export const RUBIK_METRICS = { unitsPerEm: 1000, ascent: 913, descent: 272, capHeight: 700 } as const;

/**
 * Line height of a Button label, as a multiple of its font size. MUI's own value,
 * restated here so the icon lift below and the rendered label share one number.
 */
export const BUTTON_LINE_HEIGHT = 1.75;

/**
 * How far the centre of a label's CAPITALS sits above the centre of its line
 * box, in px, so an icon centred on that line box can be lifted onto the
 * letters.
 *
 * Icons are centred geometrically (on the line box, or on the field box), and
 * a label's capitals sit above that centre, so every icon beside text read as
 * low: about 0.9px for a 13px button label, 1.3px beside a Select's value,
 * measured from pixels. Two parts, both derived rather than typed:
 *
 *   1. The font. With half-leading split evenly, the baseline sits
 *      (ascent - descent) / 2 below the line box's centre, and the capitals'
 *      centre sits capHeight / 2 above the baseline. Chromium rounds ascent and
 *      descent to whole px first, so they are rounded here too.
 *   2. The layout. Chromium FLOORS the half-leading above the text, so when
 *      (line height - ascent - descent) / 2 is fractional, the text sits higher
 *      by the remainder: 0.375px for 13px type on its 22.75px line.
 *
 * That is 0.93px for a 13px button label and 0.7px for a 12px one. The rules
 * are Chromium's (Chrome, Edge, the Electron desktop app); Firefox rounds
 * differently and may be a few tenths out. /dev/design measures the result
 * against real letters, so a change to the font or a line height shows up
 * there rather than as icons that quietly drift.
 */
export const capCentreLift = (fontSize: number, lineHeight: number, m = RUBIK_METRICS): number => {
  const ascent = Math.round((m.ascent / m.unitsPerEm) * fontSize);
  const descent = Math.round((m.descent / m.unitsPerEm) * fontSize);
  const halfLeading = (lineHeight - ascent - descent) / 2;
  const flooredRemainder = halfLeading - Math.floor(halfLeading);
  const capCentreAboveBaseline = (m.capHeight / m.unitsPerEm) * fontSize / 2;
  const boxCentreAboveBaseline = (ascent - descent) / 2;
  return Math.round((flooredRemainder + capCentreAboveBaseline - boxCentreAboveBaseline) * 100) / 100;
};

/**
 * The same, for icons centred on a FIELD (a Select chevron, an adornment, an
 * Autocomplete indicator) beside the field's text. The text element is centred
 * in the field, so uneven padding moves its line off the field's centre: by
 * (paddingBottom - paddingTop) / 2, 0.5px for the compact 4px / 5px, at any
 * field height.
 */
export const fieldIconLift = (
  paddingTop: number,
  paddingBottom: number,
  fontSize: number,
  lineHeight: number,
): number => {
  const lineAboveFieldCentre = (paddingBottom - paddingTop) / 2;
  return Math.round((lineAboveFieldCentre + capCentreLift(fontSize, lineHeight)) * 100) / 100;
};
